import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertOctagon, RefreshCw, ExternalLink, Info, Play, Loader2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { api } from '../lib/api';

interface PlatformOverview {
  activeWorlds: number;
  totalPlayerAccounts: number;
  activeAnomalyFlags: number;
}

interface DlqMessage {
  offset: string;
  partition: number;
  timestamp: string;
  key: string | null;
  value: string | null;
  topic: string;
}

/** Polls the platform overview endpoint to display general system health alongside DLQ context. */
function usePlatformHealth() {
  return useQuery<PlatformOverview>({
    queryKey: ['platform-overview-dlq'],
    queryFn: () => api.get('/analytics/platform/overview').then((r) => r.data),
    refetchInterval: 60_000,
    retry: 1,
  });
}

// Build the Grafana base URL at runtime using the current hostname so it
// Always derived at runtime so the same bundle works on any hostname
// (production, staging, dev) without baking a stale build-time value.
const GRAFANA_DLQ_URL = `${window.location.protocol}//${window.location.hostname}:7561`;

const DLQ_METRICS_URL = `${GRAFANA_DLQ_URL}/d/ukfms-service-metrics/service-metrics?orgId=1&refresh=30s&viewPanel=14`;

export function DlqPage() {
  const qc = useQueryClient();
  const { data: health, isFetching, refetch } = usePlatformHealth();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { data: dlqMessages = [], isLoading: dlqLoading, refetch: refetchDlq } = useQuery<DlqMessage[]>({
    queryKey: ['dlq-messages'],
    queryFn: () => api.get('/dlq/messages?limit=50').then((r) => r.data),
    refetchInterval: 60_000,
    retry: 1,
  });

  const replayMutation = useMutation({
    mutationFn: ({ partition, offset }: { partition: number; offset: string }) =>
      api.post(`/dlq/messages/${partition}/${offset}/replay`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dlq-messages'] }),
  });

  function toggleRow(key: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function rowKey(m: DlqMessage) { return `${m.partition}-${m.offset}`; }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <AlertOctagon className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">DLQ Monitor</h2>
            <p className="text-sm text-slate-600 mt-0.5">Dead-Letter Queue — failed Kafka messages</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white border border-white/[0.06] hover:border-white/10 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Info banner */}
      <motion.div
        className="card p-5 mb-6 border-l-2 border-amber-500/50"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-400 leading-relaxed space-y-2">
            <p>
              The <span className="text-amber-300 font-semibold">ukfms.dlq</span> Kafka topic receives
              messages that failed all retry attempts in the Event Gateway. These messages represent
              financial events that were <strong className="text-white">not processed</strong> and
              require manual investigation.
            </p>
            <p>
              DLQ accumulation is tracked as the Prometheus metric{' '}
              <code className="text-amber-300 font-mono text-[10px] px-1.5 py-0.5 bg-amber-400/10 rounded">
                ukfms_dlq_messages_total
              </code>
              . The alert <code className="text-red-300 font-mono text-[10px] px-1.5 py-0.5 bg-red-400/10 rounded">
                UkfmsDlqAccumulating
              </code>{' '}
              fires when the 24 h rate exceeds <strong className="text-white">10 messages</strong>.
            </p>
            <p className="text-slate-600">
              Direct Kafka consumer functionality is not exposed in this UI. Use the Grafana DLQ panel
              below, or inspect the topic directly via your Kafka tooling (kafkacat / kafka-topics.sh).
            </p>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <motion.div
          className="card p-5"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <div className="text-[10px] text-slate-600 tracking-widest uppercase mb-2">Active Worlds</div>
          <div className="text-2xl font-bold font-mono text-cyan-400">
            {health?.activeWorlds ?? '—'}
          </div>
        </motion.div>
        <motion.div
          className="card p-5"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="text-[10px] text-slate-600 tracking-widest uppercase mb-2">Total Holders</div>
          <div className="text-2xl font-bold font-mono text-purple-400">
            {health?.totalPlayerAccounts?.toLocaleString() ?? '—'}
          </div>
        </motion.div>
        <motion.div
          className="card p-5"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="text-[10px] text-slate-600 tracking-widest uppercase mb-2">Open Anomaly Flags</div>
          <div className={`text-2xl font-bold font-mono ${(health?.activeAnomalyFlags ?? 0) > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
            {health?.activeAnomalyFlags ?? '—'}
          </div>
        </motion.div>
      </div>

      {/* Grafana DLQ panel deep-link */}
      <motion.div
        className="card p-5 mb-6"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-semibold text-slate-300">Grafana DLQ Panel</p>
            <p className="text-xs text-slate-600 mt-0.5">
              Live DLQ rate from Prometheus — opens in a new tab
            </p>
          </div>
          <a
            href={DLQ_METRICS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-cyan-600/80 to-purple-700/80 hover:from-cyan-500/80 hover:to-purple-600/80 border border-white/[0.08] transition-all shadow-lg"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open DLQ panel
          </a>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          {/* Topic info */}
          <div>
            <p className="text-[10px] text-slate-600 tracking-widest uppercase mb-3">Topic Details</p>
            <dl className="space-y-2">
              {[
                ['Topic', 'ukfms.dlq'],
                ['Partitions', '3'],
                ['Retention', '7 days'],
                ['Alert threshold', '≥ 10 msgs / 24 h'],
              ].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between py-1.5 border-b border-white/[0.03]">
                  <dt className="text-xs text-slate-600">{label}</dt>
                  <dd className="text-xs font-mono text-slate-400">{val}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Alert details */}
          <div>
            <p className="text-[10px] text-slate-600 tracking-widest uppercase mb-3">Alert Rules</p>
            <dl className="space-y-2">
              {[
                ['Alert name', 'UkfmsDlqAccumulating'],
                ['Severity', 'critical'],
                ['Expression', 'increase(ukfms_dlq_messages_total[24h]) > 10'],
                ['Config file', 'infra/prometheus/rules/ukfms-alerts.yml'],
              ].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between py-1.5 border-b border-white/[0.03]">
                  <dt className="text-xs text-slate-600">{label}</dt>
                  <dd className="text-xs font-mono text-slate-400 text-right max-w-[200px] truncate" title={val}>{val}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </motion.div>

      {/* DLQ Message Browser */}
      <motion.div
        className="card overflow-hidden"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.04]">
          <div>
            <p className="text-sm font-semibold text-white">DLQ Message Browser</p>
            <p className="text-xs text-slate-600 mt-0.5">
              Last 50 messages from <span className="font-mono text-amber-400">ukfms.dlq</span> — replay re-publishes to <span className="font-mono text-cyan-400">ukfms.transactions</span>
            </p>
          </div>
          <button
            onClick={() => refetchDlq()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-white border border-white/[0.06] hover:border-white/10 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${dlqLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {dlqLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-600 text-xs">
            <Loader2 className="w-4 h-4 animate-spin" /> Connecting to Kafka…
          </div>
        ) : dlqMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-700">
            <AlertOctagon className="w-8 h-8 mb-3" />
            <p className="text-sm font-medium">DLQ is empty</p>
            <p className="text-xs mt-1">No failed messages — all systems nominal.</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.04]">
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-600 w-6" />
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-600">Partition / Offset</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-600">Timestamp</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-600">Key</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-600">Value preview</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {dlqMessages.map((m) => {
                const key = rowKey(m);
                const expanded = expandedRows.has(key);
                const isReplaying = replayMutation.isPending && replayMutation.variables?.offset === m.offset;
                let prettyValue: string;
                try { prettyValue = JSON.stringify(JSON.parse(m.value ?? ''), null, 2); }
                catch { prettyValue = m.value ?? ''; }
                return (
                  <>
                    <tr
                      key={key}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => toggleRow(key)}
                    >
                      <td className="px-4 py-3 text-slate-600">
                        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-400">{m.partition}/{m.offset}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {new Date(Number(m.timestamp)).toLocaleString('en-GB', { hour12: false })}
                      </td>
                      <td className="px-4 py-3 text-slate-400 font-mono max-w-[160px] truncate">{m.key ?? <span className="text-slate-700">null</span>}</td>
                      <td className="px-4 py-3 text-slate-500 max-w-[280px] truncate font-mono">{(m.value ?? '').slice(0, 80)}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => replayMutation.mutate({ partition: m.partition, offset: m.offset })}
                          disabled={isReplaying}
                          className="flex items-center gap-1 ml-auto px-2.5 py-1 rounded-lg text-[10px] font-semibold text-emerald-300 border border-emerald-500/20 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors disabled:opacity-40"
                        >
                          {isReplaying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                          Replay
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr key={`${key}-expanded`} className="bg-white/[0.01]">
                        <td colSpan={6} className="px-5 py-3">
                          <pre className="text-[10px] text-slate-400 font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto bg-black/20 rounded-lg p-3">
                            {prettyValue}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </motion.div>
    </div>
  );
}
